/**
 * Main App Component
 * Flight Game - Multiplayer airline management game
 */

import { lazy, Suspense, useEffect } from 'react'
import { LoginScreen } from './components/login/login-screen'
import { ToastContainer } from './components/common/toast-container'
import { LoadingPage, LoadingVoice } from './components/common/loading'
import { ConnectionLostNotice } from './components/game/common/notices/connection-lost-notice'
import { authService } from './services/authentication/auth-service'
import { useAuthStore } from './domains/auth'

// Import domain message router to trigger auto-registration with NakamaService
// This must be imported before any Nakama connections are made
import './domains/domain-message-router'

// GameScreen (and its three.js/LiveKit dependency tree) is code-split so the
// login screen renders without downloading the full game bundle
const importGameScreen = () => import('./components/game/game-screen')
const GameScreen = lazy(() =>
  importGameScreen().then((module) => ({ default: module.GameScreen }))
)

function App() {
  const connectionStatus = useAuthStore((state) => state.connectionStatus);
  const isConnected = connectionStatus === 'connected';

  // Prefetch the game chunk while the user is still on the login screen so
  // the login -> game transition doesn't wait on a network round trip
  useEffect(() => {
    importGameScreen();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Toast notifications */}
      <ToastContainer />

      {/* The one voice of every wait, on the page from the start: the code's, the join's snapshot's, the globe's engine's, said once. */}
      <LoadingVoice />

      {/* Connection lost: opens on its own store once every reconnect attempt
          has failed. Reconnect goes back to the way in, keeping the saved
          sign-in: the game's stores reset, the notice with them. */}
      <ConnectionLostNotice onReconnect={() => void authService.handleUnexpectedDisconnect()} />

      {/* Conditional Rendering: Show GameScreen when connected, otherwise LoginScreen */}
      <div className="flex-1">
        {isConnected ? (
          <Suspense fallback={<LoadingPage />}>
            <GameScreen />
          </Suspense>
        ) : (
          <LoginScreen />
        )}
      </div>

    </div>
  )
}

export default App
